import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    signal,
    SimpleChanges,
} from '@angular/core';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { map, startWith, take } from 'rxjs/operators';

import { GetProductDetailQuery, ProductPreviewFragment } from '../../common/gql/graphql';
import { ActiveService } from '../../../core/providers/active-order/active.service';
import {
    getShippingRestrictions,
    ShippingRestriction,
} from '../../../common/utils/get-shipping-restrictions';

@Component({
    selector: 'kb-base-variant-selector',
    template: ``,
    styles: [``],
    standalone: true,
})
export class BaseVariantSelectorComponent implements OnInit, OnChanges, OnDestroy {
    @Input() productPreview: ProductPreviewFragment;
    @Input() product: NonNullable<GetProductDetailQuery['product']> | null;
    @Output() addToCartClick = new EventEmitter<{ productVariantId: string; quantity: number }>();
    @Output() updateOrderLineClick = new EventEmitter<{ orderLineId: string; quantity: number }>();
    @Output() addToWishlistClick = new EventEmitter<{ productVariantId: string; wishlistId?: string }>();
    inCartQty: { [variantId: string]: number } = {};
    adjustedQty: { [variantId: string]: number } = {};
    wishlistData = signal<{ [variantId: string]: Array<{ id: string; name?: string }> }>({});
    shippingInfo$: Observable<ShippingRestriction | undefined>;
    private product$ = new Subject<GetProductDetailQuery['product']>();

    protected subscription: Subscription;

    constructor(protected activeService: ActiveService, protected changeDetector: ChangeDetectorRef) {}

    ngOnInit() {
        this.shippingInfo$ = combineLatest([this.product$, this.activeService.defaultShippingLocation$]).pipe(
            map(([product, location]) => getShippingRestrictions(location, product?.shippingRestrictions)),
        );
        this.subscription = this.activeService.activeOrder$.subscribe((order) => {
            this.inCartQty = {};
            for (const line of order?.lines ?? []) {
                if (!line.customFields?.freeGiftDescription) {
                    this.inCartQty[line.productVariant.id] = line.quantity;
                }
            }
            this.changeDetector.markForCheck();
        });

        this.subscription.add(
            combineLatest([
                this.activeService.activeUserWishlists$,
                this.product$.pipe(startWith(this.product)),
            ]).subscribe(([wishlists, product]) => {
                if (!product || !wishlists) {
                    return;
                }
                const wishlistData: { [variantId: string]: Array<{ id: string; name?: string }> } = {};
                for (const variant of product.variants) {
                    wishlistData[variant.id] = [];
                    for (const wishlist of wishlists) {
                        if (wishlist.items.find((i) => i.productVariantId === variant.id)) {
                            wishlistData[variant.id].push({
                                id: wishlist.id,
                                name: wishlist.name,
                            });
                        }
                    }
                }
                this.wishlistData.set(wishlistData);
            }),
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('product' in changes && this.product) {
            this.product$.next(this.product);
        }
    }

    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    manuallySetQuantity(variantId: string, qty: number) {
        this.activeService.activeOrder$.pipe(take(1)).subscribe((activeOrder) => {
            const orderLine = activeOrder?.lines.find((l) => l.productVariant.id === variantId);
            if (orderLine) {
                this.updateOrderLineClick.emit({
                    orderLineId: orderLine.id,
                    quantity: qty,
                });
            }
            delete this.adjustedQty[variantId];
        });
    }

    variantAppearsInWishlists(variantId?: string) {
        if (!variantId) {
            return [];
        }
        const wishlistData = this.wishlistData();
        return wishlistData[variantId];
    }
}
